import type { RelationshipAssertion } from "@/entities/form-metadata";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";

/**
 * UBE focus tracking for `FamilyMemberGroup`'s per-relation Name fields (Mother Given Name,
 * Father Surname, …). Unlike the top-level Given Name/Surname fields — one non-repeating
 * instance per form, identified by a constant `objectId` — these repeat once per relation, so
 * identity is compositional (`relation`/`fieldName`) rather than a constant string; see
 * `docs/engineering/ube.md`.
 *
 * Returns a lookup function rather than tracking a single assertion directly, so it can be
 * called once per assertion inside a render loop without violating the Rules of Hooks.
 */
export function useFamilyMemberFieldFocusTracking(): (
    relation: string | null | undefined,
    assertion: RelationshipAssertion,
) => { onFocus: () => void } {
    const track = useTrackSearchFormInteraction();

    return (relation, assertion) => ({
        onFocus: () =>
            track(
                {
                    action: "focus",
                    ...(relation && { relation }),
                    fieldName: assertion,
                },
                { dedup: true },
            ),
    });
}
